import { SupabaseClient } from "@supabase/supabase-js";
import { logException, SentryFeatureTags } from "@punchup/sentry";
import dayjs from "@punchup/utils/dayjs";
import { Database } from "../../supabase.types";
import type {
  SupportConversationInsert,
  SupportConversationRow,
  SupportMessageInsert,
  SupportMessageRow,
  SupportSearchEventInsert,
} from "../../models/support";

/**
 * Support-assistant conversations. A conversation's messages are the exact
 * Anthropic message params of every turn, replayed verbatim on the next one;
 * a turn is written only after it succeeds, in one insert.
 */

type Client = SupabaseClient<Database>;

function fail(
  action: string,
  error: unknown,
  extra?: Record<string, unknown>
): never {
  logException(error, {
    tags: { feature: SentryFeatureTags.SupportAssistant, action },
    extra,
  });
  throw error;
}

export async function getSupportConversation(
  supabase: Client,
  conversationId: string
): Promise<SupportConversationRow | null> {
  const { data, error } = await supabase
    .from("support_conversations")
    .select("*")
    .eq("id", conversationId)
    .maybeSingle();

  if (error) fail("getSupportConversation", error, { conversationId });
  return data;
}

/** Someone's most recent conversations, newest activity first. */
export async function listSupportConversations(
  supabase: Client,
  options: { userId: string; limit: number }
): Promise<
  Pick<SupportConversationRow, "id" | "title" | "status" | "updated_at">[]
> {
  const { data, error } = await supabase
    .from("support_conversations")
    .select("id, title, status, updated_at")
    .eq("user_id", options.userId)
    .order("updated_at", { ascending: false })
    .limit(options.limit);

  if (error) fail("listSupportConversations", error, options);
  return data ?? [];
}

/** Every message of a conversation, in the order it was sent. */
export async function getSupportConversationMessages(
  supabase: Client,
  conversationId: string
): Promise<SupportMessageRow[]> {
  const { data, error } = await supabase
    .from("support_messages")
    .select("*")
    .eq("conversation_id", conversationId)
    .order("seq", { ascending: true });

  if (error) fail("getSupportConversationMessages", error, { conversationId });
  return data ?? [];
}

/**
 * Writes one finished turn. A new conversation (`conversation` given) is
 * created with it and deleted again if the messages cannot be written, so a
 * failed first turn leaves nothing behind. The messages go in one insert — a
 * conversation never holds half a turn; search events are analytics and go
 * after.
 */
export async function insertSupportTurn(
  supabase: Client,
  turn: {
    conversationId: string;
    conversation?: SupportConversationInsert;
    messages: SupportMessageInsert[];
    searchEvents: SupportSearchEventInsert[];
  }
): Promise<SupportMessageRow[]> {
  const { conversationId } = turn;

  if (turn.conversation) {
    const { error } = await supabase
      .from("support_conversations")
      .insert({ ...turn.conversation, id: conversationId });
    if (error)
      fail("insertSupportTurn.conversation", error, { conversationId });
  }

  // Only the answer cites cases, and a bulk insert sends NULL for any column a
  // row leaves out. Every row names cited_case_ids (NOT NULL) itself instead of
  // relying on PostgREST's `missing=default`, which older versions ignore.
  const rows = turn.messages.map((message) => ({
    cited_case_ids: [],
    ...message,
  }));
  const { data, error } = await supabase
    .from("support_messages")
    .insert(rows)
    .select("*");

  if (error || !data) {
    if (turn.conversation) {
      const { error: cleanupError } = await supabase
        .from("support_conversations")
        .delete()
        .eq("id", conversationId);
      // The turn's own failure is what the caller hears; an empty
      // conversation left behind is only reported.
      if (cleanupError) {
        logException(cleanupError, {
          tags: {
            feature: SentryFeatureTags.SupportAssistant,
            action: "insertSupportTurn.cleanup",
          },
          extra: { conversationId },
        });
      }
    }
    fail("insertSupportTurn.messages", error, { conversationId });
  }

  if (turn.searchEvents.length > 0) {
    const { error: searchError } = await supabase
      .from("support_search_events")
      .insert(turn.searchEvents);
    if (searchError) {
      logException(searchError, {
        tags: {
          feature: SentryFeatureTags.SupportAssistant,
          action: "insertSupportTurn.searchEvents",
        },
        extra: { conversationId },
      });
    }
  }

  if (!turn.conversation) {
    await supabase
      .from("support_conversations")
      .update({ updated_at: dayjs.utc().toISOString() })
      .eq("id", conversationId);
  }

  return [...data].sort((a, b) => a.seq - b.seq);
}

/** Links a conversation to the case written from it. */
export async function setSupportConversationResultingCase(
  supabase: Client,
  conversationId: string,
  caseId: string
): Promise<void> {
  const { error } = await supabase
    .from("support_conversations")
    .update({ resulting_case_id: caseId })
    .eq("id", conversationId);

  if (error) {
    fail("setSupportConversationResultingCase", error, {
      conversationId,
      caseId,
    });
  }
}
